import type { HelpTopic } from '../../modules/core/index.js'

/** The Executive Insights add-on's help topics (docs/plans/in-app-help-plan.md), one per screen. */
export const INSIGHTS_HELP_TOPICS: readonly HelpTopic[] = [
  {
    id: 'executive_insights.portfolio',
    title: 'The portfolio',
    seats: ['operator', 'facility_administrator'],
    summary: 'How every facility is doing over the span, in the figures that matter most, with the one sliding most first.',
    sections: [
      {
        heading: 'What you see',
        points: [
          'Six tiles: the whole portfolio\'s average score, Gold share, call-outs per 100 shifts, fill from the roster at release, hours per patient day worked and retention, each with its change since the month before and its months in a stroke.',
          'The table ranks facilities by the change in the figure you choose. A worsening sorts to the top, so the facility that needs a call is first. Coral marks a change for the worse, never a colour alone.',
          'The grid shows every facility on one figure at a glance. A row or a tile opens the facility\'s Dashboard.',
        ],
      },
      {
        heading: 'Where the numbers come from',
        points: [
          'Each facility\'s month is added up once its scoring period closes, and each day after a release: the finals, the reported shifts, the census, the released month and its board.',
          'The portfolio\'s figures are added up from every facility\'s sums, never from their averages, so a large facility counts for more.',
          'A month with nothing to say shows a dash: no finals yet, no shifts reported, no census recorded.',
          'The CSV button saves every facility\'s months as one spreadsheet, every figure a column.',
        ],
      },
      {
        heading: 'Your facilities',
        seats: ['facility_administrator'],
        points: ['You see the facilities you administer. The portfolio is theirs added up.'],
      },
    ],
    related: ['executive_insights.reliability', 'roster.dashboard'],
  },
  {
    id: 'executive_insights.reliability',
    title: 'Reliability over time',
    seats: ['operator', 'facility_administrator'],
    summary: 'Whether the scores are moving, where they sit, and which behaviors are moving them.',
    sections: [
      {
        heading: 'What you see',
        points: [
          'The score trend: the portfolio as a dashed line, the band between the best and worst facility, and up to four facilities you choose as lines of their own.',
          'A dotted gold rule marks a month a setting changed: scoring weights, tier thresholds, scheduling rules, behaviors, an add-on. The list under the chart says what changed and where, never who changed it or to what.',
          'Where the scores sit: the share of workers in each score band, month by month, so the bottom lifting shows even when the average is flat.',
          'Each behavior on its own, with the weight it carries, the chosen facilities against the portfolio.',
        ],
      },
      {
        heading: 'Good to know',
        points: [
          'A final score is set when a period closes. A month still running is not drawn until its close.',
          'Point at a chart for the exact values of a month.',
        ],
      },
    ],
    related: ['executive_insights.portfolio', 'scoring.worker-score'],
  },
  {
    id: 'executive_insights.tiers',
    title: 'Tiers',
    seats: ['operator', 'facility_administrator'],
    summary: 'The tier each close set, and how workers moved between tiers.',
    sections: [
      {
        heading: 'What you see',
        points: [
          'The tier mix at each close, as a share of the workers.',
          'How many moved up and how many down at each close, and the closes added up: held, new hires under the 90-day rule, provisional tiers held.',
        ],
      },
      { heading: 'Good to know', points: ['A tier changes only when a period closes. A month still running is not drawn.', 'How long a worker keeps Gold is a per-worker reading and is on their own history.'] },
    ],
    related: ['executive_insights.reliability'],
  },
  {
    id: 'executive_insights.attendance',
    title: 'Attendance',
    seats: ['operator', 'facility_administrator'],
    summary: 'Call-outs, no-shows and late arrivals per 100 scheduled shifts, and when the call-outs fall.',
    sections: [
      {
        heading: 'What you see',
        points: [
          'Call-outs per 100 scheduled shifts by month, the chosen facilities against the portfolio. The facilities are the ones chosen on the Reliability screen.',
          'When call-outs happen: a grid by weekday and shift type over the span, the count written in each cell.',
          'No-shows and late arrivals per 100 shifts, and charting completion in each facility\'s latest month.',
        ],
      },
      { heading: 'Good to know', points: ['Late is judged against each facility\'s own grace minutes, as the score judges it.', 'A shift\'s type is the scheduled seat\'s where there is one, else the catalogue type its start is nearest.'] },
    ],
    related: ['executive_insights.coverage'],
  },
  {
    id: 'executive_insights.coverage',
    title: 'Coverage',
    seats: ['operator', 'facility_administrator'],
    summary: 'Who filled each released month\'s seats, what ran short, and the rules the runs strained.',
    sections: [
      {
        heading: 'What you see',
        points: [
          'Who filled the month: the roster at release, the open board\'s offers, first come, the supervisor, census adds and Clinical Bridge, with the seats that ran short apart.',
          'Fill at release for each facility\'s latest released month against its minimum; a bar in coral is below it.',
          'The board\'s traffic by month, and the placements the runs flagged for overtime and days in a row, per 100 workers.',
        ],
      },
      { heading: 'Good to know', points: ['Only a released month is read here: before the release there is no board.'] },
    ],
    related: ['schedule.build-the-month'],
  },
  {
    id: 'executive_insights.census',
    title: 'Census and workforce',
    seats: ['operator', 'facility_administrator'],
    summary: 'Hours against patient days, the census against the plan, and the roster by employment type.',
    sections: [
      {
        heading: 'What you see',
        points: [
          'Hours per patient day scheduled, worked and target by month, over the facilities that recorded a census.',
          'Patient days recorded against the planned census over the same days.',
          'The roster by employment type at each month\'s end with the hires and leavers, and each facility\'s FTE gap against the workforce plan.',
        ],
      },
      { heading: 'Good to know', points: ['PPD is patient days: one resident in a bed for one day. HPPD is productive hours over patient days.', 'Retention by month and the staffed-days calendar are on each facility\'s Reporting tab.'] },
    ],
    related: ['schedule.staffing-report', 'schedule.workforce-plan'],
  },
  {
    id: 'executive_insights.quarter',
    title: 'The last quarter',
    seats: ['operator', 'facility_administrator'],
    summary: 'The last ninety days of what is kept that long: Clinical Bridge callouts and what the deliveries reached.',
    sections: [
      { heading: 'What you see', points: ['The Clinical Bridge funnel: opened, answered, filled, expired, cancelled, and the median minutes to fill.', 'Deliveries by channel: sent, failed, and the parts a text ran to.'] },
      { heading: 'Good to know', points: ['These records are kept ninety days, so nothing older is offered.', 'The deliveries are the client\'s as a whole, so a facility administrator does not see them.'] },
    ],
    related: ['executive_insights.portfolio'],
  },
]
